/*
  RSG Motion System
  Reuses the theme's existing GSAP 3.12.5 instance (flu.chunks.gsap) instead of
  loading a second copy. Adds Lenis smooth scroll + ScrollTrigger (self-hosted,
  version-matched to 3.12.5) on top of it.

  Step 2 of the build: smooth scroll only. Reveals / marquee / pin / magnetic /
  cursor / view-transitions are added in later steps on top of this skeleton.
*/
(function () {
  'use strict';

  // ---- Idempotent guard -----------------------------------------------
  if (window.RSGMotion && window.RSGMotion.__initDone) return;
  window.RSGMotion = window.RSGMotion || {};
  window.RSGMotion.__initDone = true;

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-rsg-motion-src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === 'true') resolve();
        else existing.addEventListener('load', resolve, { once: true });
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.setAttribute('data-rsg-motion-src', src);
      s.onload = function () {
        s.setAttribute('data-loaded', 'true');
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ---- Scroll-lock bridge ----------------------------------------------
  // The theme's cart/nav/search/modal drawers use the bundled body-scroll-lock
  // package (assets/theme.js), which is private to that module's closure and
  // isn't exposed on window. Rather than patch theme.js at its ~12 call sites,
  // watch the one observable side effect of the desktop lock path: body gets
  // style="overflow: hidden". (iOS lock uses touch-event prevention instead of
  // an inline style, but Lenis already defers to native touch scrolling there,
  // so smooth-scroll pause/resume isn't needed on that path.)
  function bridgeScrollLock(lenis) {
    var wasLocked = document.body.style.overflow === 'hidden';
    var observer = new MutationObserver(function () {
      var isLocked = document.body.style.overflow === 'hidden';
      if (isLocked === wasLocked) return;
      wasLocked = isLocked;
      if (isLocked) lenis.stop();
      else lenis.start();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return observer;
  }

  // ---- Smooth scroll init -----------------------------------------------
  function initSmoothScroll(gsap) {
    if (window.lenis) return; // already running (e.g. re-entrant call)

    // ScrollTrigger 3.12.5 is a classic UMD build: it self-registers onto
    // window.gsap if present at load time. Expose the theme's GSAP instance
    // globally just long enough for that auto-registration to happen, so the
    // whole site shares a single GSAP instance instead of two.
    window.gsap = gsap;

    loadScriptOnce(window.RSGMotionAssets.scrollTrigger).then(function () {
      var ScrollTrigger = window.ScrollTrigger;
      ScrollTrigger.config({ ignoreMobileResize: true });

      var lenis = new window.Lenis({
        duration: 1.1,
        easing: function (t) {
          // expo-out
          return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        },
        smoothWheel: true,
        syncTouch: false // native touch scrolling on mobile/tablet
      });

      gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);

      lenis.on('scroll', ScrollTrigger.update);

      window.lenis = lenis;
      window.RSGMotion.scrollTrigger = ScrollTrigger;
      bridgeScrollLock(lenis);

      document.documentElement.classList.add('motion-ready');

      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      });
    });
  }

  function boot() {
    document.documentElement.classList.add('motion-ready');

    if (reducedMotion) {
      // Reduced-motion users get the normal, native, fully scrollable page —
      // no Lenis, no ScrollTrigger, nothing to initialize.
      return;
    }

    if (!window.flu || !window.flu.chunks || !window.flu.chunks.gsap) {
      // Theme's chunk loader isn't present for some reason — fail open to a
      // normal readable/scrollable page rather than break navigation.
      return;
    }

    import(window.flu.chunks.gsap).then(function (mod) {
      initSmoothScroll(mod.gsap);
    });
  }

  ready(boot);

  // ---- Theme editor lifecycle -------------------------------------------
  // No per-section triggers exist yet at this step, but ScrollTrigger's
  // measurements depend on total document height, which the editor can
  // change on every section add/remove/reorder while previewing.
  document.addEventListener('shopify:section:load', function () {
    if (window.RSGMotion.scrollTrigger) window.RSGMotion.scrollTrigger.refresh();
  });
})();
